import {
  EArmadaType,
  EContractType,
  EMethodPaymentSPOT,
} from '@/types/kontrakSpot'

import { LocalKey } from '@utils/storage'

import { ConfirmationDismissType } from '@components/confirmation'
import { NotificationPosition } from '@components/notifications'
import { CommonDredgingApprovalStatus } from '@elements/dredging-approval-action/dredging-approval-action.types'
import { ModalDismissType } from '@elements/index'

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api'
const PANGSU_API_BASE_URL = process.env.REACT_APP_PANGSU_API_BASE_URL
const CAPTCHA_SITE_KEY = process.env.REACT_APP_CAPTCHA_SITE_KEY || 'captcha_key'

const OAUTH_ClIENT_ID = process.env.REACT_APP_SSO_CLIENT_ID || 'client_id'
const OAUTH_CLIENT_SECRET =
  process.env.REACT_APP_SSO_CLIENT_SECRET || 'client_secret'
const OAUTH_REDIRECT_URI =
  process.env.REACT_APP_SSO_CALLBACK_URL || '/oauth-callback'
const OAUTH_REDIRECT_LOGOUT =
  process.env.REACT_APP_SSO_AUTH_LOGOUT_URL || '/logout'

const OAUTH_AUTH_API = process.env.REACT_APP_SSO_AUTH_URL || '/sso/auth'
const OAUTH_TOKEN_API =
  process.env.REACT_APP_SSO_ACCESS_TOKEN_URL || '/sso/token'

const OAUTH_POST_LOGOUT_REDIRECT =
  process.env.REACT_APP_SSO_POST_LOGOUT_REDIRECT_URL || '/post-logout'

const NEW_DOMAIN = process.env.REACT_APP_NEW_DOMAIN || '/pangsu'

// Kill-switch untuk rollout Klaim Asuransi V2 (link ke app baru di NEW_DOMAIN).
// Nama env disamakan dengan flag `cr_asuransi` di crm-bag-frontend
// (FEATURE_CR_ASURANSI), REACT_APP_ prefix wajib supaya CRA meng-inline-nya.
// OFF secara default supaya menu tidak muncul sebelum rollout siap di app baru.
const ENABLE_KLAIM_ASURANSI_V2 =
  process.env.REACT_APP_FEATURE_CR_ASURANSI === 'true'

const DateFormat = {
  base: 'yyyy-MM-dd',
  detail: 'yyyy-MM-dd HH:mm:ss',
  table: 'MM-dd-yyyy',
  bbo: 'dd/MM/yyyy',
  bboDetail: 'dd/MM/yyyy HH:mm:ss',
  bboNoTime: 'dd/MM/yyyy HH:mm',
  insurance: 'dd-MMM-yyyy HH:mm',
}

const storageKey = {
  authToken: new LocalKey<string | null | undefined>('auth-token', ''),
}

enum Action {
  DETAIL = 'view',
  EDIT = 'edit',
  CREATE = 'create',
}

const usersRole = {
  sysadmin: 'sysadmin',
  staffKeuangan: 'staff_keuangan',
  managerKeuangan: 'manager_keuangan',
  staffKemitraan: 'staff_kemitraan',
  managerKemitraan: 'manager_kemitraan',
  staffPu: 'staff_pu',
  managerPu: 'manager_pu',
  staffPenjualan: 'staff_penjualan',
  managerPenjualan: 'manager_penjualan',
  vpNiaga: 'vp_niaga',
  vpKeuangan: 'vp_keuangan',
  staffOperasi: 'staff_operasi',
  managerOperasi: 'manager_operasi',
  vpOperasi: 'vp_operasi',
  mitraKso: 'mitra_kso',
  agenAsuransi: 'agen_asuransi',
  internalAuditor: 'internal_auditor',

  // pangsu
  pangsu_staff_operasi: 'pangsu_staff_operasi',
  pangsu_asmen_operasi: 'pangsu_asmen_operasi',
  pangsu_staff_keuangan: 'pangsu_staff_keuangan',
  pangsu_asmen_keuangan: 'pangsu_asmen_keuangan',
  pangsu_manajer_cabang: 'pangsu_manajer_cabang',

  // tjb
  tjb_staff_operasi: 'tjb_staff_operasi',
  tjb_asmen_operasi: 'tjb_asmen_operasi',
  tjb_staff_keuangan: 'tjb_staff_keuangan',
  tjb_asmen_keuangan: 'tjb_asmen_keuangan',
  tjb_manajer_cabang: 'tjb_manajer_cabang',
}

const moduleName = {
  dashboard: 'dashboard',
  dashboardPenjualan: 'dashboard_penjualan',
  dashboardNiaga: 'dashboard_niaga',
  dashboardBBO: 'dashboard_bbo',
  userManagement: 'user_management',
  kontrakKso: 'kontrak_kso',
  kontrakPelanggan: 'kontrak_pelanggan',
  routeMitraKso: 'route_mitra_kso',
  routePemberiKerja: 'route_pemberi_kerja',
  entriShipment: 'entri_shipment',
  alokasiKapal: 'alokasi_kapal',
  agendaRakor: 'agenda_rakor',
  deviasiShipment: 'deviasi_shipment',
  shipmentInstruction: 'shipment_instruction',
  shipmentOperation: 'shipment_operation',
  arsipShipment: 'arsip_operation',
  penagihan: 'penagihan',
  penagihanMitraKso: 'penagihan_mitra_kso',
  penagihanPemberiKerja: 'penagihan_pemberi_kerja',
  // notaDinasPenagihan: 'nota_dinas_penagihan',
  mdPemberiKerja: 'md_pemberi_kerja',
  mdMitraKso: 'md_mitra_kso',
  mdPemasok: 'md_pemasok',
  mdKapal: 'md_kapal',
  mdTermin: 'md_termin',
  mdModa: 'md_moda',
  mdPol: 'md_pol',
  mdPod: 'md_pod',
  mdTemplate: 'md_template',
  variables: 'variables',
  signature: 'signature',
  mdKapalDredging: 'md_kapal_dredging',
  pengaturanBbmBulanan: 'pengaturan_bbm_bulanan',
  bbmBerjalan: 'bbm_berjalan',
  kontrakPemberiKerja: 'kontrak_pemberi_kerja',
  mdRouteShipment: 'md_route_shipment',
  profile: 'profile',
  sailingInstruction: 'sailing_instruction',
  shipment: 'shipment',
  kontrakMitraKSO: 'kontrak_mitra_kso',
  operation: 'operation',
  spal: 'spal',
  penagihanMitra: 'penagihan_mitra',
  requestSI: 'request_SI',
  updateOrder: 'update_order',
  pembayaranMitra: 'pembayaran_mitra',
  documentOS: 'document_operation_shipment',
  pendapatanBatuBara: 'pendapatan_batubara',
  rasioOperasionalKso: 'rasio_operasional_kso',
  shipmentTepatWaktu: 'shipment_tepat_waktu',
  amandemenSpal: 'amandemen_spal',
  laporanCashIn: 'laporan_cash_in',
  laporanCashOut: 'laporan_cash_out',
  laporanShipmentBelumBertarif: 'laporan_shipment_belum_bertarif',
  laporanShipmentTBN: 'laporan_shipment_tbn',
  laporanShipmentKSO: 'laporan_shipment_kso',
  laporanTonaseBL: 'laporan_tonase_bl',
  laporanIdlePemasok: 'laporan_idle_pemasok',
  laporanPengangkutanBatubara: 'laporan_pengangkutan_batubara',
  laporanKelengkapanDokumen: 'laporan_kelengkapan_dokumen',
  laporanShipmentPltu: 'laporan_shipment_pltu',
  laporanIdlePltu: 'laporan_idle_pltu',
  shiplink: 'shiplink',
  mdFormula: 'md_formula',
  mdHargaTriwulan: 'md_harga_triwulan',
  asuransi: 'asuransi',
  klaimAsuransi: 'klaim_asuransi',
  arsipAsuransi: 'arsip_asuransi',
  listAsuransi: 'list_asuransi',
  monitoring: 'monitoring',
  listSla: 'list_sla',
  dredgingContractPemberiKerja: 'dredging_contract_pemberi_kerja',
  dredgingContractMitraKSO: 'dredging_contract_mitra_kso',
  dredgingOperationPlanning: 'dredging_operation_planning',
  dredgingOperationOperation: 'dredging_operation_operation',
  dredgingOperationSPK3: 'dredging_operation_spk3',
  dredgingOperationOlahGerak: 'dredging_operation_olah_gerak',
  dredgingOperationDaily: 'dredging_operation_daily',
  dredgingOperationWeekly: 'dredging_operation_weekly',
  dredgingOperationOverall: 'dredging-operation-overall',
  dredgingBCPemberiKerja: 'dredging_bc_pemberi_kerja',
  dredgingBCMitra: 'dredging_bc_mitra',

  // pangsu
  mdJetty: 'md_jetty',
  mdJisdor: 'md_jisdor',
  kontrakPemberiKerjaPangsu: 'kontrak_pemberi_kerja_pangsu',
  kontrakMitraPangsu: 'kontrak_mitra_pangsu',
  jadwalShipmentPangsu: 'jadwal_shipment_pangsu',
  operasiShipmentPangsu: 'operasi_shipment_pangsu',
  operasiPemeliharaanPangsu: 'operasi_pemeliharaan_pangsu',
  penagihanPangsu: 'penagihan_pangsu',
  pembayaranPangsu: 'pembayaran_pangsu',

  // TJB
  kontrakPemberiKerjaTJB: 'kontrak_pemberi_kerja_tjb',
  kontrakMitraTJB: 'kontrak_mitra_tjb',
  coordinationMeetingTJB: 'coordination_meeting_tjb',
  shipmentPlanningTJB: 'shipment_planning_tjb',
  shipmentOperationTJB: 'shipment_operation_tjb',
  customerInvoiceTJB: 'customer_invoice_tjb',
  vendorBillTJB: 'vendor_bill_tjb',
}

const roleAction = {
  create: 'create',
  read: 'read',
  update: 'update',
  delete: 'delete',
  activeInActive: 'active_in_active',
  readOwned: 'read_owned',
  updateOwned: 'update_owned',
  approve: 'approve',
  reject: 'reject',
  createOwned: 'create_owned',
}

const roleGroup = {
  C: [roleAction.create],
  R: [roleAction.read],
  U: [roleAction.update],
  D: [roleAction.delete],
  CR: [roleAction.create, roleAction.read],
  CU: [roleAction.create, roleAction.update],
  CD: [roleAction.create, roleAction.delete],
  RU: [roleAction.read, roleAction.update],
  RD: [roleAction.read, roleAction.delete],
  UD: [roleAction.update, roleAction.delete],
  CRU: [roleAction.create, roleAction.read, roleAction.update],
  CUD: [roleAction.create, roleAction.update, roleAction.delete],
  RUD: [roleAction.read, roleAction.update, roleAction.delete],
  CRUD: [
    roleAction.create,
    roleAction.read,
    roleAction.update,
    roleAction.delete,
  ],
  CRD: [
    roleAction.create,
    roleAction.read,
    roleAction.update,
    roleAction.delete,
  ],
  RO: [roleAction.readOwned],
  ROUO: [roleAction.read, roleAction.readOwned, roleAction.updateOwned],
  A: [roleAction.read, roleAction.approve],
  AU: [roleAction.read, roleAction.update, roleAction.approve],
  RE: [roleAction.read, roleAction.reject],
  ARE: [roleAction.read, roleAction.approve, roleAction.reject],
  RUARE: [
    roleAction.read,
    roleAction.update,
    roleAction.approve,
    roleAction.reject,
  ],
  CRUDARE: [
    roleAction.create,
    roleAction.read,
    roleAction.update,
    roleAction.delete,
    roleAction.approve,
    roleAction.reject,
  ],
  RARE: [
    roleAction.read,
    roleAction.delete,
    roleAction.approve,
    roleAction.reject,
  ],
  RUOAR: [
    roleAction.read,
    roleAction.updateOwned,
    roleAction.approve,
    roleAction.reject,
  ],
  CRUCOUO: [
    roleAction.create,
    roleAction.read,
    roleAction.update,
    roleAction.createOwned,
    roleAction.updateOwned,
  ],
  CRCOUOARE: [
    roleAction.create,
    roleAction.read,
    roleAction.update,
    roleAction.createOwned,
    roleAction.updateOwned,
    roleAction.approve,
    roleAction.reject,
  ],
  ROUOARE: [
    roleAction.readOwned,
    roleAction.updateOwned,
    roleAction.read,
    roleAction.approve,
    roleAction.reject,
  ],
}

const publicRoleMatiks = [moduleName.profile]

const roleMatriks = {
  [usersRole.sysadmin]: {
    [moduleName.dashboard]: roleGroup.R,
    [moduleName.dashboardPenjualan]: roleGroup.R,
    [moduleName.dashboardNiaga]: roleGroup.R,
    [moduleName.dashboardBBO]: roleGroup.R,
    [moduleName.deviasiShipment]: roleGroup.CRUD,
    [moduleName.userManagement]: [...roleGroup.CRUD, roleAction.activeInActive],
    [moduleName.kontrakPelanggan]: roleGroup.CRUD,
    [moduleName.routeMitraKso]: roleGroup.CRUD,
    [moduleName.routePemberiKerja]: roleGroup.CRUD,
    [moduleName.entriShipment]: roleGroup.CRUD,
    [moduleName.alokasiKapal]: roleGroup.CRUDARE,
    [moduleName.updateOrder]: roleGroup.RU,
    [moduleName.agendaRakor]: roleGroup.CRUD,
    [moduleName.shipmentInstruction]: roleGroup.CRUDARE,
    [moduleName.shipmentOperation]: roleGroup.RD,
    [moduleName.shipmentOperation]: roleGroup.RD,
    [moduleName.penagihan]: roleGroup.CRUD,
    [moduleName.penagihanMitraKso]: roleGroup.ARE,
    [moduleName.penagihanPemberiKerja]: roleGroup.ARE,
    // [moduleName.notaDinasPenagihan]: roleGroup.CRUD,
    [moduleName.mdPemberiKerja]: roleGroup.CRUD,
    [moduleName.mdMitraKso]: roleGroup.CRUD,
    [moduleName.mdPemasok]: roleGroup.CRUD,
    [moduleName.mdKapal]: roleGroup.CRUD,
    [moduleName.mdTermin]: roleGroup.CRUD,
    [moduleName.mdModa]: roleGroup.CRUD,
    [moduleName.mdPol]: roleGroup.CRUD,
    [moduleName.mdPod]: roleGroup.CRUD,
    [moduleName.mdKapalDredging]: roleGroup.CRUD,
    [moduleName.mdJetty]: roleGroup.CRUD,
    [moduleName.mdRouteShipment]: roleGroup.CRUDARE,
    [moduleName.mdTemplate]: roleGroup.CRUD,
    [moduleName.variables]: roleGroup.CRUD,
    [moduleName.signature]: roleGroup.CRUD,
    [moduleName.bbmBerjalan]: roleGroup.CRUD,
    [moduleName.pengaturanBbmBulanan]: [],
    [moduleName.kontrakPemberiKerja]: roleGroup.CRUDARE,
    [moduleName.sailingInstruction]: roleGroup.CRUDARE,
    [moduleName.shipment]: roleGroup.CRUD,
    [moduleName.kontrakMitraKSO]: roleGroup.CRUDARE,
    [moduleName.operation]: roleGroup.CRUD,
    [moduleName.spal]: roleGroup.RUOAR,
    [moduleName.bbmBerjalan]: roleGroup.CRU,
    [moduleName.requestSI]: roleGroup.CRU,
    [moduleName.pembayaranMitra]: roleGroup.ARE,
    [moduleName.documentOS]: roleGroup.CRUDARE,
    [moduleName.pendapatanBatuBara]: roleGroup.R,
    [moduleName.rasioOperasionalKso]: roleGroup.R,
    [moduleName.shipmentTepatWaktu]: roleGroup.R,
    [moduleName.amandemenSpal]: roleGroup.CRUD,
    [moduleName.laporanCashIn]: roleGroup.R,
    [moduleName.laporanCashOut]: roleGroup.R,
    [moduleName.laporanShipmentBelumBertarif]: roleGroup.R,
    [moduleName.laporanShipmentTBN]: roleGroup.R,
    [moduleName.laporanShipmentKSO]: roleGroup.R,
    [moduleName.laporanTonaseBL]: roleGroup.R,
    [moduleName.laporanIdlePemasok]: roleGroup.R,
    [moduleName.laporanPengangkutanBatubara]: roleGroup.R,
    [moduleName.laporanKelengkapanDokumen]: roleGroup.R,
    [moduleName.laporanShipmentPltu]: roleGroup.R,
    [moduleName.laporanIdlePltu]: roleGroup.R,
    [moduleName.shiplink]: roleGroup.R,
    [moduleName.mdFormula]: roleGroup.CRD,
    [moduleName.dashboardBBO]: roleGroup.R,
    [moduleName.mdHargaTriwulan]: roleGroup.CRUD,
    [moduleName.asuransi]: roleGroup.R,
    [moduleName.klaimAsuransi]: roleGroup.R,
    [moduleName.listAsuransi]: roleGroup.R,
    [moduleName.arsipAsuransi]: roleGroup.R,
    [moduleName.listSla]: roleGroup.R,
    [moduleName.dredgingContractPemberiKerja]: roleGroup.CRCOUOARE,
    [moduleName.dredgingContractMitraKSO]: roleGroup.CRCOUOARE,
    [moduleName.dredgingOperationPlanning]: roleGroup.CRCOUOARE,
    [moduleName.dredgingOperationOperation]: roleGroup.R,
    [moduleName.dredgingOperationSPK3]: roleGroup.CRUDARE,
    [moduleName.dredgingOperationOlahGerak]: roleGroup.CRUDARE,
    [moduleName.dredgingOperationDaily]: roleGroup.CRCOUOARE,
    [moduleName.dredgingOperationWeekly]: roleGroup.CRCOUOARE,
    [moduleName.dredgingBCPemberiKerja]: roleGroup.CRUDARE,
    [moduleName.dredgingBCMitra]: roleGroup.CRUDARE,
    // [moduleName.pangsu]: roleGroup.CRUD,
    [moduleName.mdJetty]: roleGroup.CRUD,
    [moduleName.mdJisdor]: roleGroup.CRUD,
    [moduleName.kontrakMitraPangsu]: roleGroup.CRUD,
    [moduleName.kontrakPemberiKerjaPangsu]: roleGroup.CRUD,
    [moduleName.jadwalShipmentPangsu]: roleGroup.CRUD,
    [moduleName.operasiShipmentPangsu]: roleGroup.CRUD,
    [moduleName.operasiPemeliharaanPangsu]: roleGroup.CRUD,
    [moduleName.penagihanPangsu]: roleGroup.CRUD,
    [moduleName.pembayaranPangsu]: roleGroup.CRUD,
    [moduleName.kontrakPemberiKerjaTJB]: roleGroup.CRUD,
    [moduleName.kontrakMitraTJB]: roleGroup.CRUD,
    [moduleName.coordinationMeetingTJB]: roleGroup.CRUD,
    [moduleName.shipmentPlanningTJB]: roleGroup.CRUD,
    [moduleName.shipmentOperationTJB]: roleGroup.CRUD,
    [moduleName.customerInvoiceTJB]: roleGroup.CRUD,
    [moduleName.vendorBillTJB]: roleGroup.CRUD,
  },
  [usersRole.staffPu]: {
    [moduleName.dashboard]: roleGroup.R,
    [moduleName.mdPemberiKerja]: roleGroup.R,
    [moduleName.mdPol]: roleGroup.R,
    [moduleName.mdPod]: roleGroup.R,
    [moduleName.kontrakPemberiKerja]: roleGroup.R,
    [moduleName.kontrakMitraKSO]: roleGroup.R,
    [moduleName.shipment]: roleGroup.CRU,
    [moduleName.deviasiShipment]: roleGroup.CRUD,
    [moduleName.sailingInstruction]: roleGroup.AU,
    [moduleName.agendaRakor]: roleGroup.CRUD,
    [moduleName.shipmentOperation]: roleGroup.R,
    [moduleName.penagihanPemberiKerja]: roleGroup.R,
    [moduleName.penagihanMitraKso]: roleGroup.ARE,
    [moduleName.bbmBerjalan]: roleGroup.CRUD,
    [moduleName.pembayaranMitra]: roleGroup.ARE,
    [moduleName.documentOS]: roleGroup.ARE,
    [moduleName.shipmentTepatWaktu]: roleGroup.R,
    [moduleName.laporanKelengkapanDokumen]: roleGroup.R,
    [moduleName.laporanShipmentTBN]: roleGroup.R,
    [moduleName.laporanPengangkutanBatubara]: roleGroup.R,
    [moduleName.laporanIdlePemasok]: roleGroup.R,
    [moduleName.laporanIdlePltu]: roleGroup.R,
    [moduleName.laporanShipmentPltu]: roleGroup.R,
    [moduleName.laporanShipmentKSO]: roleGroup.R,
    [moduleName.shiplink]: roleGroup.R,
    [moduleName.listSla]: roleGroup.R,
    [moduleName.dredgingContractPemberiKerja]: roleGroup.R,
    [moduleName.dredgingContractMitraKSO]: roleGroup.R,
    [moduleName.dredgingOperationPlanning]: roleGroup.R,
    [moduleName.mdTemplate]: roleGroup.CRUD,
    [moduleName.signature]: roleGroup.CRUD,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  [usersRole.managerPu]: {
    [moduleName.dashboard]: roleGroup.R,
    [moduleName.dashboardPenjualan]: roleGroup.R,
    [moduleName.dashboardNiaga]: roleGroup.R,
    [moduleName.mdPemberiKerja]: roleGroup.R,
    [moduleName.mdPol]: roleGroup.R,
    [moduleName.mdPod]: roleGroup.R,
    [moduleName.kontrakPemberiKerja]: roleGroup.R,
    [moduleName.kontrakMitraKSO]: roleGroup.R,
    [moduleName.mdRouteShipment]: roleGroup.R,
    [moduleName.shipment]: roleGroup.CRUD,
    [moduleName.sailingInstruction]: roleGroup.ARE,
    [moduleName.agendaRakor]: roleGroup.CRUD,
    [moduleName.shipmentOperation]: roleGroup.ARE,
    [moduleName.penagihanPemberiKerja]: roleGroup.R,
    [moduleName.penagihanMitraKso]: roleGroup.ARE,
    [moduleName.bbmBerjalan]: roleGroup.CRUD,
    [moduleName.pembayaranMitra]: roleGroup.ARE,
    [moduleName.shipmentTepatWaktu]: roleGroup.R,
    [moduleName.laporanKelengkapanDokumen]: roleGroup.R,
    [moduleName.laporanShipmentTBN]: roleGroup.R,
    [moduleName.laporanPengangkutanBatubara]: roleGroup.R,
    [moduleName.laporanIdlePemasok]: roleGroup.R,
    [moduleName.laporanIdlePltu]: roleGroup.R,
    [moduleName.laporanShipmentPltu]: roleGroup.R,
    [moduleName.laporanShipmentKSO]: roleGroup.R,
    [moduleName.shiplink]: roleGroup.R,
    [moduleName.asuransi]: roleGroup.R,
    [moduleName.klaimAsuransi]: roleGroup.R,
    [moduleName.listSla]: roleGroup.R,
    [moduleName.dredgingContractPemberiKerja]: roleGroup.R,
    [moduleName.dredgingContractMitraKSO]: roleGroup.R,
    [moduleName.dredgingOperationPlanning]: roleGroup.ARE,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  [usersRole.staffPenjualan]: {
    [moduleName.dashboard]: roleGroup.R,
    [moduleName.dashboardBBO]: roleGroup.R,
    [moduleName.mdPemberiKerja]: roleGroup.CRUD,
    [moduleName.mdPol]: roleGroup.CRUD,
    [moduleName.mdPod]: roleGroup.CRUD,
    [moduleName.mdModa]: roleGroup.CRUD,
    [moduleName.mdTermin]: roleGroup.CRUD,
    [moduleName.mdPemasok]: roleGroup.CRUD,
    [moduleName.mdMitraKso]: roleGroup.R,
    [moduleName.updateOrder]: roleGroup.RU,
    [moduleName.kontrakPemberiKerja]: roleGroup.CRUD,
    [moduleName.kontrakMitraKSO]: roleGroup.R,
    [moduleName.mdRouteShipment]: roleGroup.CRUD,
    [moduleName.shipment]: roleGroup.ROUO,
    [moduleName.shipmentOperation]: roleGroup.R,
    [moduleName.penagihanPemberiKerja]: roleGroup.R,
    [moduleName.penagihanMitraKso]: roleGroup.ARE,
    [moduleName.bbmBerjalan]: roleGroup.R,
    [moduleName.mdFormula]: roleGroup.CRD,
    [moduleName.pembayaranMitra]: roleGroup.R,
    [moduleName.pendapatanBatuBara]: roleGroup.R,
    [moduleName.rasioOperasionalKso]: roleGroup.R,
    [moduleName.shipmentTepatWaktu]: roleGroup.R,
    [moduleName.laporanCashIn]: roleGroup.R,
    [moduleName.laporanCashOut]: roleGroup.R,
    [moduleName.laporanKelengkapanDokumen]: roleGroup.R,
    [moduleName.laporanShipmentBelumBertarif]: roleGroup.R,
    [moduleName.shiplink]: roleGroup.R,
    [moduleName.mdFormula]: roleGroup.CRD,
    [moduleName.dashboardBBO]: roleGroup.R,
    [moduleName.listSla]: roleGroup.R,
    [moduleName.dredgingContractPemberiKerja]: roleGroup.CRUCOUO,
    [moduleName.dredgingContractMitraKSO]: roleGroup.R,
    [moduleName.dredgingOperationPlanning]: roleGroup.R,
    [moduleName.dredgingOperationOperation]: roleGroup.R,
    [moduleName.dredgingOperationSPK3]: roleGroup.R,
    [moduleName.dredgingOperationOlahGerak]: roleGroup.R,
    [moduleName.dredgingBCPemberiKerja]: roleGroup.CRU,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  [usersRole.managerPenjualan]: {
    [moduleName.dashboard]: roleGroup.R,
    [moduleName.dashboardBBO]: roleGroup.R,
    [moduleName.dashboardPenjualan]: roleGroup.R,
    [moduleName.dashboardNiaga]: roleGroup.R,
    [moduleName.mdPemberiKerja]: roleGroup.CRUD,
    [moduleName.mdPol]: roleGroup.CRUD,
    [moduleName.mdPod]: roleGroup.CRUD,
    [moduleName.mdModa]: roleGroup.CRUD,
    [moduleName.mdTermin]: roleGroup.CRUD,
    [moduleName.mdFormula]: roleGroup.CRD,
    [moduleName.mdPemasok]: roleGroup.R,
    [moduleName.mdMitraKso]: roleGroup.R,
    [moduleName.kontrakPemberiKerja]: roleGroup.RARE,
    [moduleName.kontrakMitraKSO]: roleGroup.R,
    [moduleName.mdRouteShipment]: roleGroup.CRUDARE,
    [moduleName.shipment]: roleGroup.R,
    [moduleName.updateOrder]: roleGroup.RU,
    [moduleName.shipmentOperation]: roleGroup.R,
    [moduleName.penagihanPemberiKerja]: roleGroup.R,
    [moduleName.penagihanMitraKso]: roleGroup.ARE,
    [moduleName.bbmBerjalan]: roleGroup.R,
    [moduleName.pembayaranMitra]: roleGroup.R,
    [moduleName.pendapatanBatuBara]: roleGroup.R,
    [moduleName.rasioOperasionalKso]: roleGroup.R,
    [moduleName.shipmentTepatWaktu]: roleGroup.R,
    [moduleName.laporanCashIn]: roleGroup.R,
    [moduleName.laporanCashOut]: roleGroup.R,
    [moduleName.laporanKelengkapanDokumen]: roleGroup.R,
    [moduleName.laporanShipmentBelumBertarif]: roleGroup.R,
    [moduleName.shiplink]: roleGroup.R,
    [moduleName.mdFormula]: roleGroup.CRD,
    [moduleName.dashboardBBO]: roleGroup.R,
    [moduleName.listSla]: roleGroup.R,
    [moduleName.dredgingContractPemberiKerja]: roleGroup.ROUOARE,
    [moduleName.dredgingContractMitraKSO]: roleGroup.R,
    [moduleName.dredgingOperationPlanning]: roleGroup.R,
    [moduleName.dredgingOperationOperation]: roleGroup.R,
    [moduleName.dredgingOperationSPK3]: roleGroup.R,
    [moduleName.dredgingOperationOlahGerak]: roleGroup.R,
    [moduleName.dredgingBCPemberiKerja]: roleGroup.ARE,
    [moduleName.dredgingBCMitra]: roleGroup.ARE,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  [usersRole.staffKemitraan]: {
    [moduleName.dashboard]: roleGroup.R,
    [moduleName.mdPemberiKerja]: roleGroup.R,
    [moduleName.mdPol]: roleGroup.R,
    [moduleName.mdPod]: roleGroup.R,
    [moduleName.mdModa]: roleGroup.R,
    [moduleName.mdTermin]: roleGroup.CRUD,
    [moduleName.mdKapal]: roleGroup.CRUD,
    [moduleName.mdPemasok]: roleGroup.R,
    [moduleName.mdMitraKso]: roleGroup.CRUD,
    [moduleName.kontrakPemberiKerja]: roleGroup.R,
    [moduleName.kontrakMitraKSO]: roleGroup.CRUD,
    [moduleName.mdRouteShipment]: roleGroup.CRUD,
    [moduleName.alokasiKapal]: roleGroup.CRU,
    [moduleName.shipment]: roleGroup.RU,
    [moduleName.sailingInstruction]: roleGroup.R,
    [moduleName.agendaRakor]: roleGroup.CRUD,
    [moduleName.spal]: roleGroup.RUOAR,
    [moduleName.shipmentOperation]: roleGroup.R,
    [moduleName.penagihanPemberiKerja]: roleGroup.R,
    [moduleName.penagihanMitraKso]: roleGroup.R,
    [moduleName.bbmBerjalan]: roleGroup.CRUD,
    [moduleName.pembayaranMitra]: roleGroup.RUARE,
    [moduleName.amandemenSpal]: roleGroup.CRUD,
    [moduleName.shipmentTepatWaktu]: roleGroup.R,
    [moduleName.laporanCashOut]: roleGroup.R,
    [moduleName.laporanKelengkapanDokumen]: roleGroup.R,
    [moduleName.laporanShipmentBelumBertarif]: roleGroup.R,
    [moduleName.laporanTonaseBL]: roleGroup.R,
    [moduleName.laporanShipmentTBN]: roleGroup.R,
    [moduleName.laporanShipmentPltu]: roleGroup.R,
    [moduleName.laporanShipmentKSO]: roleGroup.R,
    [moduleName.shiplink]: roleGroup.R,
    [moduleName.asuransi]: roleGroup.R,
    [moduleName.klaimAsuransi]: roleGroup.R,
    [moduleName.mdKapalDredging]: roleGroup.CRUD,
    [moduleName.dredgingContractPemberiKerja]: roleGroup.R,
    [moduleName.dredgingContractMitraKSO]: roleGroup.CRUCOUO,
    [moduleName.dredgingOperationPlanning]: roleGroup.R,
    [moduleName.dredgingOperationOperation]: roleGroup.R,
    [moduleName.dredgingOperationSPK3]: roleGroup.R,
    [moduleName.dredgingOperationOlahGerak]: roleGroup.R,
    [moduleName.dredgingBCMitra]: roleGroup.ARE,
    [moduleName.mdTemplate]: roleGroup.CRUD,
    [moduleName.signature]: roleGroup.CRUD,

    // PANGSU
    [moduleName.kontrakPemberiKerjaPangsu]: roleGroup.R,
    [moduleName.kontrakMitraPangsu]: roleGroup.R,
    [moduleName.penagihanPangsu]: roleGroup.R,
    [moduleName.pembayaranPangsu]: roleGroup.R,

    // TJB
    [moduleName.kontrakPemberiKerjaTJB]: roleGroup.R,
    [moduleName.kontrakMitraTJB]: roleGroup.R,
    [moduleName.coordinationMeetingTJB]: roleGroup.R,
    [moduleName.shipmentPlanningTJB]: roleGroup.R,
    [moduleName.shipmentOperationTJB]: roleGroup.R,
    [moduleName.customerInvoiceTJB]: roleGroup.R,
    [moduleName.vendorBillTJB]: roleGroup.R,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  [usersRole.managerKemitraan]: {
    [moduleName.dashboard]: roleGroup.R,
    [moduleName.dashboardPenjualan]: roleGroup.R,
    [moduleName.dashboardNiaga]: roleGroup.R,
    [moduleName.mdPemberiKerja]: roleGroup.R,
    [moduleName.mdPol]: roleGroup.CRUD,
    [moduleName.mdPod]: roleGroup.CRUD,
    [moduleName.mdModa]: roleGroup.CRUD,
    [moduleName.mdTermin]: roleGroup.CRUD,
    [moduleName.mdKapal]: roleGroup.CRUD,
    [moduleName.mdPemasok]: roleGroup.R,
    [moduleName.mdMitraKso]: roleGroup.CRUD,
    [moduleName.kontrakPemberiKerja]: roleGroup.R,
    [moduleName.kontrakMitraKSO]: roleGroup.ARE,
    [moduleName.shipment]: roleGroup.ARE,
    [moduleName.alokasiKapal]: roleGroup.ARE,
    [moduleName.mdRouteShipment]: roleGroup.R,
    [moduleName.sailingInstruction]: roleGroup.R,
    [moduleName.agendaRakor]: roleGroup.CRUD,
    [moduleName.spal]: roleGroup.ARE,
    [moduleName.shipmentOperation]: roleGroup.R,
    [moduleName.penagihanPemberiKerja]: roleGroup.R,
    [moduleName.penagihanMitraKso]: roleGroup.R,
    [moduleName.bbmBerjalan]: roleGroup.CRUD,
    [moduleName.pembayaranMitra]: roleGroup.ARE,
    [moduleName.shipmentTepatWaktu]: roleGroup.R,
    [moduleName.laporanCashOut]: roleGroup.R,
    [moduleName.laporanKelengkapanDokumen]: roleGroup.R,
    [moduleName.laporanShipmentBelumBertarif]: roleGroup.R,
    [moduleName.laporanTonaseBL]: roleGroup.R,
    [moduleName.laporanShipmentTBN]: roleGroup.R,
    [moduleName.laporanShipmentPltu]: roleGroup.R,
    [moduleName.laporanShipmentKSO]: roleGroup.R,
    [moduleName.shiplink]: roleGroup.R,
    [moduleName.asuransi]: roleGroup.R,
    [moduleName.klaimAsuransi]: roleGroup.R,
    [moduleName.arsipAsuransi]: roleGroup.R,
    [moduleName.listSla]: roleGroup.R,
    [moduleName.mdKapalDredging]: roleGroup.R,
    [moduleName.dredgingContractPemberiKerja]: roleGroup.R,
    [moduleName.dredgingContractMitraKSO]: roleGroup.ARE,
    [moduleName.dredgingOperationOperation]: roleGroup.R,
    [moduleName.dredgingOperationSPK3]: roleGroup.R,
    [moduleName.dredgingOperationOlahGerak]: roleGroup.R,
    [moduleName.dredgingBCMitra]: roleGroup.ARE,

    // PANGSU
    [moduleName.kontrakPemberiKerjaPangsu]: roleGroup.R,
    [moduleName.kontrakMitraPangsu]: roleGroup.R,
    [moduleName.penagihanPangsu]: roleGroup.R,
    [moduleName.pembayaranPangsu]: roleGroup.R,

    // TJB
    [moduleName.kontrakPemberiKerjaTJB]: roleGroup.R,
    [moduleName.kontrakMitraTJB]: roleGroup.R,
    [moduleName.coordinationMeetingTJB]: roleGroup.R,
    [moduleName.shipmentPlanningTJB]: roleGroup.R,
    [moduleName.shipmentOperationTJB]: roleGroup.R,
    [moduleName.customerInvoiceTJB]: roleGroup.R,
    [moduleName.vendorBillTJB]: roleGroup.R,
  },
  [usersRole.vpNiaga]: {
    [moduleName.dashboard]: roleGroup.R,
    [moduleName.dashboardBBO]: roleGroup.R,
    [moduleName.dashboardPenjualan]: roleGroup.R,
    [moduleName.dashboardNiaga]: roleGroup.R,
    [moduleName.mdPemberiKerja]: roleGroup.R,
    [moduleName.mdPol]: roleGroup.R,
    [moduleName.mdPod]: roleGroup.R,
    [moduleName.mdModa]: roleGroup.R,
    [moduleName.mdTermin]: roleGroup.R,
    [moduleName.mdKapal]: roleGroup.R,
    [moduleName.mdPemasok]: roleGroup.R,
    [moduleName.mdMitraKso]: roleGroup.R,
    [moduleName.kontrakPemberiKerja]: roleGroup.R,
    [moduleName.kontrakMitraKSO]: roleGroup.R,
    [moduleName.mdRouteShipment]: roleGroup.R,
    [moduleName.shipment]: roleGroup.R,
    [moduleName.sailingInstruction]: roleGroup.RUARE,
    [moduleName.agendaRakor]: roleGroup.R,
    [moduleName.spal]: roleGroup.ARE,
    [moduleName.shipmentOperation]: roleGroup.R,
    [moduleName.penagihanPemberiKerja]: roleGroup.ARE,
    [moduleName.penagihanMitraKso]: roleGroup.ARE,
    [moduleName.bbmBerjalan]: roleGroup.R,
    [moduleName.pembayaranMitra]: roleGroup.ARE,
    [moduleName.pendapatanBatuBara]: roleGroup.R,
    [moduleName.rasioOperasionalKso]: roleGroup.R,
    [moduleName.shipmentTepatWaktu]: roleGroup.R,
    [moduleName.laporanCashIn]: roleGroup.R,
    [moduleName.laporanCashOut]: roleGroup.R,
    [moduleName.laporanKelengkapanDokumen]: roleGroup.R,
    [moduleName.laporanShipmentBelumBertarif]: roleGroup.R,
    [moduleName.laporanTonaseBL]: roleGroup.R,
    [moduleName.laporanShipmentTBN]: roleGroup.R,
    [moduleName.laporanPengangkutanBatubara]: roleGroup.R,
    [moduleName.laporanIdlePemasok]: roleGroup.R,
    [moduleName.laporanIdlePltu]: roleGroup.R,
    [moduleName.laporanShipmentPltu]: roleGroup.R,
    [moduleName.laporanShipmentKSO]: roleGroup.R,
    [moduleName.shiplink]: roleGroup.R,
    [moduleName.listSla]: roleGroup.R,
    [moduleName.dredgingBCPemberiKerja]: roleGroup.ARE,
    [moduleName.dredgingBCMitra]: roleGroup.ARE,

    // TJB
    [moduleName.kontrakPemberiKerjaTJB]: roleGroup.R,
    [moduleName.kontrakMitraTJB]: roleGroup.R,
    [moduleName.coordinationMeetingTJB]: roleGroup.R,
    [moduleName.shipmentPlanningTJB]: roleGroup.R,
    [moduleName.shipmentOperationTJB]: roleGroup.R,
    [moduleName.customerInvoiceTJB]: roleGroup.R,
    [moduleName.vendorBillTJB]: roleGroup.R,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  [usersRole.vpKeuangan]: {
    [moduleName.dashboard]: roleGroup.R,
    [moduleName.asuransi]: roleGroup.R,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  // Check lagi shipmentOperation dan penagihanPemberiKerja
  [usersRole.staffOperasi]: {
    [moduleName.dashboard]: roleGroup.R,
    [moduleName.mdKapal]: roleGroup.CRUD,
    [moduleName.mdRouteShipment]: roleGroup.R,
    [moduleName.kontrakPemberiKerja]: roleGroup.R,
    [moduleName.shipment]: roleGroup.R,
    [moduleName.shipmentOperation]: roleGroup.R,
    [moduleName.penagihanPemberiKerja]: roleGroup.R,
    [moduleName.bbmBerjalan]: roleGroup.R,
    [moduleName.requestSI]: roleGroup.CRU,
    [moduleName.documentOS]: roleGroup.U,
    [moduleName.shiplink]: roleGroup.R,
    [moduleName.asuransi]: roleGroup.R,
    [moduleName.klaimAsuransi]: roleGroup.R,
    [moduleName.laporanKelengkapanDokumen]: roleGroup.R,
    [moduleName.shipmentTepatWaktu]: roleGroup.R,
    [moduleName.listSla]: roleGroup.R,
    [moduleName.mdKapalDredging]: roleGroup.CRUD,
    [moduleName.dredgingOperationPlanning]: roleGroup.R,
    [moduleName.dredgingOperationOperation]: roleGroup.R,
    [moduleName.dredgingOperationSPK3]: roleGroup.CRU,
    [moduleName.dredgingOperationOlahGerak]: roleGroup.CRU,
    [moduleName.dredgingOperationDaily]: roleGroup.RU,
    [moduleName.dredgingOperationWeekly]: roleGroup.CRU,
    [moduleName.dredgingOperationOverall]: roleGroup.R,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  [usersRole.managerOperasi]: {
    [moduleName.dashboard]: roleGroup.R,
    [moduleName.dashboardPenjualan]: roleGroup.R,
    [moduleName.dashboardNiaga]: roleGroup.R,
    [moduleName.mdKapal]: roleGroup.CRUD,
    [moduleName.mdRouteShipment]: roleGroup.R,
    [moduleName.shipment]: roleGroup.R,
    [moduleName.shipmentOperation]: roleGroup.ARE,
    [moduleName.penagihanPemberiKerja]: roleGroup.ARE,
    [moduleName.bbmBerjalan]: roleGroup.R,
    [moduleName.requestSI]: roleGroup.CRU,
    [moduleName.documentOS]: roleGroup.ARE,
    [moduleName.shiplink]: roleGroup.R,
    [moduleName.laporanKelengkapanDokumen]: roleGroup.R,
    [moduleName.shipmentTepatWaktu]: roleGroup.R,
    [moduleName.klaimAsuransi]: roleGroup.R,
    [moduleName.listAsuransi]: roleGroup.R,
    [moduleName.listSla]: roleGroup.R,
    [moduleName.dredgingOperationPlanning]: roleGroup.R,
    [moduleName.dredgingOperationOperation]: roleGroup.R,
    [moduleName.dredgingOperationSPK3]: roleGroup.ARE,
    [moduleName.dredgingOperationOlahGerak]: roleGroup.ARE,
    [moduleName.dredgingOperationDaily]: roleGroup.ARE,
    [moduleName.dredgingOperationWeekly]: roleGroup.ARE,
    [moduleName.dredgingOperationOverall]: roleGroup.R,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  [usersRole.vpOperasi]: {
    [moduleName.dashboard]: roleGroup.R,
    [moduleName.dashboardPenjualan]: roleGroup.R,
    [moduleName.dashboardNiaga]: roleGroup.R,
    [moduleName.mdKapal]: roleGroup.R,
    [moduleName.mdRouteShipment]: roleGroup.R,
    [moduleName.shipment]: roleGroup.R,
    [moduleName.shipmentOperation]: roleGroup.ARE,
    [moduleName.penagihanPemberiKerja]: roleGroup.ARE,
    [moduleName.bbmBerjalan]: roleGroup.R,
    [moduleName.requestSI]: roleGroup.CRU,
    [moduleName.shiplink]: roleGroup.R,
    [moduleName.laporanKelengkapanDokumen]: roleGroup.R,
    [moduleName.shipmentTepatWaktu]: roleGroup.R,
    [moduleName.listSla]: roleGroup.R,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  // Check lagi shipment operation dan penagihan mitra kso
  [usersRole.mitraKso]: {
    [moduleName.dashboard]: roleGroup.R,
    [moduleName.mdKapal]: roleGroup.CRUD,
    [moduleName.mdKapalDredging]: roleGroup.CRUD,
    [moduleName.routePemberiKerja]: roleGroup.R,
    [moduleName.routeMitraKso]: roleGroup.CRUD,
    [moduleName.shipment]: roleGroup.RU,
    [moduleName.spal]: roleGroup.ROUO,
    [moduleName.shipmentOperation]: roleGroup.R,
    [moduleName.requestSI]: roleGroup.CR,
    [moduleName.pembayaranMitra]: roleGroup.RU,
    [moduleName.alokasiKapal]: roleGroup.ROUO,
    [moduleName.documentOS]: roleGroup.U,
    [moduleName.asuransi]: roleGroup.R,
    [moduleName.klaimAsuransi]: roleGroup.R,
    [moduleName.dredgingOperationOperation]: roleGroup.R,
    [moduleName.dredgingOperationSPK3]: roleGroup.CRU,
    [moduleName.dredgingOperationOlahGerak]: roleGroup.CRU,
    [moduleName.dredgingOperationDaily]: roleGroup.RU,
    [moduleName.dredgingOperationWeekly]: roleGroup.CRU,
    [moduleName.dredgingOperationOverall]: roleGroup.R,
    [moduleName.dredgingOperationPlanning]: roleGroup.RU,
    [moduleName.dredgingBCMitra]: roleGroup.CRU,
    [moduleName.mdJetty]: roleGroup.R,
    [moduleName.mdJisdor]: roleGroup.R,

    // PANGSU
    [moduleName.mdMitraKso]: roleGroup.RU,
    [moduleName.kontrakMitraPangsu]: roleGroup.R,
    [moduleName.operasiShipmentPangsu]: roleGroup.R,
    [moduleName.operasiPemeliharaanPangsu]: roleGroup.R,
    [moduleName.pembayaranPangsu]: roleGroup.R,

    // TJB
    [moduleName.kontrakMitraTJB]: roleGroup.R,
    [moduleName.coordinationMeetingTJB]: roleGroup.R,
    [moduleName.shipmentOperationTJB]: roleGroup.R,
    [moduleName.vendorBillTJB]: roleGroup.R,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  [usersRole.staffKeuangan]: {
    [moduleName.shipmentOperation]: roleGroup.R,
    [moduleName.shiplink]: roleGroup.R,
    [moduleName.asuransi]: roleGroup.R,
    [moduleName.klaimAsuransi]: roleGroup.R,
    [moduleName.listAsuransi]: roleGroup.R,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  [usersRole.managerKeuangan]: {
    [moduleName.shipmentOperation]: roleGroup.R,
    [moduleName.shiplink]: roleGroup.R,
    [moduleName.asuransi]: roleGroup.R,
    [moduleName.klaimAsuransi]: roleGroup.R,
    [moduleName.listAsuransi]: roleGroup.R,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  [usersRole.agenAsuransi]: {
    [moduleName.asuransi]: roleGroup.R,
    [moduleName.klaimAsuransi]: roleGroup.R,
    [moduleName.listAsuransi]: roleGroup.R,
    // [moduleName.shiplink]: roleGroup.R,
    // [moduleName.shipmentOperation]: roleGroup.R,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  [usersRole.internalAuditor]: {
    [moduleName.dashboard]: roleGroup.R,
    [moduleName.dashboardPenjualan]: roleGroup.R,
    [moduleName.dashboardNiaga]: roleGroup.R,
    [moduleName.dashboardBBO]: roleGroup.R,
    [moduleName.deviasiShipment]: roleGroup.R,
    [moduleName.userManagement]: roleGroup.R,
    [moduleName.kontrakKso]: roleGroup.R,
    [moduleName.kontrakPelanggan]: roleGroup.R,
    [moduleName.routeMitraKso]: roleGroup.R,
    [moduleName.routePemberiKerja]: roleGroup.R,
    [moduleName.entriShipment]: roleGroup.R,
    [moduleName.alokasiKapal]: roleGroup.R,
    [moduleName.updateOrder]: roleGroup.R,
    [moduleName.agendaRakor]: roleGroup.R,
    [moduleName.shipmentInstruction]: roleGroup.R,
    [moduleName.shipmentOperation]: roleGroup.R,
    [moduleName.shipmentOperation]: roleGroup.R,
    [moduleName.penagihan]: roleGroup.R,
    [moduleName.penagihanMitraKso]: roleGroup.R,
    [moduleName.penagihanPemberiKerja]: roleGroup.R,
    // [moduleName.notaDinasPenagihan]: roleGroup.CRUD,
    [moduleName.mdPemberiKerja]: roleGroup.R,
    [moduleName.mdMitraKso]: roleGroup.R,
    [moduleName.mdPemasok]: roleGroup.R,
    [moduleName.mdKapal]: roleGroup.R,
    [moduleName.mdTermin]: roleGroup.R,
    [moduleName.mdModa]: roleGroup.R,
    [moduleName.mdPol]: roleGroup.R,
    [moduleName.mdPod]: roleGroup.R,
    [moduleName.mdKapalDredging]: roleGroup.R,
    [moduleName.mdRouteShipment]: roleGroup.R,
    [moduleName.mdTemplate]: roleGroup.R,
    [moduleName.variables]: roleGroup.R,
    [moduleName.signature]: roleGroup.R,
    [moduleName.bbmBerjalan]: roleGroup.R,
    [moduleName.pengaturanBbmBulanan]: [],
    [moduleName.kontrakPemberiKerja]: roleGroup.R,
    [moduleName.sailingInstruction]: roleGroup.R,
    [moduleName.shipment]: roleGroup.R,
    [moduleName.kontrakMitraKSO]: roleGroup.R,
    [moduleName.operation]: roleGroup.R,
    [moduleName.spal]: roleGroup.R,
    [moduleName.bbmBerjalan]: roleGroup.R,
    [moduleName.requestSI]: roleGroup.R,
    [moduleName.pembayaranMitra]: roleGroup.R,
    [moduleName.documentOS]: roleGroup.R,
    [moduleName.pendapatanBatuBara]: roleGroup.R,
    [moduleName.rasioOperasionalKso]: roleGroup.R,
    [moduleName.shipmentTepatWaktu]: roleGroup.R,
    [moduleName.amandemenSpal]: roleGroup.R,
    [moduleName.laporanCashIn]: roleGroup.R,
    [moduleName.laporanCashOut]: roleGroup.R,
    [moduleName.laporanShipmentBelumBertarif]: roleGroup.R,
    [moduleName.laporanShipmentTBN]: roleGroup.R,
    [moduleName.laporanShipmentKSO]: roleGroup.R,
    [moduleName.laporanTonaseBL]: roleGroup.R,
    [moduleName.laporanIdlePemasok]: roleGroup.R,
    [moduleName.laporanPengangkutanBatubara]: roleGroup.R,
    [moduleName.laporanKelengkapanDokumen]: roleGroup.R,
    [moduleName.laporanShipmentPltu]: roleGroup.R,
    [moduleName.laporanIdlePltu]: roleGroup.R,
    [moduleName.shiplink]: roleGroup.R,
    [moduleName.mdFormula]: roleGroup.R,
    [moduleName.dashboardBBO]: roleGroup.R,
    [moduleName.mdHargaTriwulan]: roleGroup.R,
    [moduleName.asuransi]: roleGroup.R,
    [moduleName.klaimAsuransi]: roleGroup.R,
    [moduleName.listAsuransi]: roleGroup.R,
    [moduleName.listSla]: roleGroup.R,
    [moduleName.dredgingContractPemberiKerja]: roleGroup.R,
    [moduleName.dredgingContractMitraKSO]: roleGroup.R,
    [moduleName.dredgingOperationPlanning]: roleGroup.R,
    [moduleName.dredgingOperationOperation]: roleGroup.R,
    [moduleName.dredgingOperationSPK3]: roleGroup.R,
    [moduleName.dredgingOperationOlahGerak]: roleGroup.R,
    [moduleName.dredgingOperationDaily]: roleGroup.R,
    [moduleName.dredgingOperationWeekly]: roleGroup.R,
    [moduleName.dredgingBCPemberiKerja]: roleGroup.R,
    [moduleName.dredgingBCMitra]: roleGroup.R,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },

  // pangsu
  [usersRole.pangsu_staff_operasi]: {
    [moduleName.mdKapal]: roleGroup.CRUD,
    [moduleName.mdMitraKso]: roleGroup.CRUD,
    [moduleName.mdPemberiKerja]: roleGroup.CRUD,
    [moduleName.mdJetty]: roleGroup.CRUD,
    [moduleName.kontrakPemberiKerjaPangsu]: roleGroup.CRUD,
    [moduleName.kontrakMitraPangsu]: roleGroup.CRUD,
    [moduleName.jadwalShipmentPangsu]: roleGroup.CRUD,
    [moduleName.operasiShipmentPangsu]: roleGroup.CRUD,
    [moduleName.operasiPemeliharaanPangsu]: roleGroup.CRUD,
    [moduleName.penagihanPangsu]: roleGroup.R,
    [moduleName.pembayaranPangsu]: roleGroup.R,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  [usersRole.pangsu_asmen_operasi]: {
    [moduleName.mdKapal]: roleGroup.R,
    [moduleName.mdMitraKso]: roleGroup.R,
    [moduleName.mdPemberiKerja]: roleGroup.R,
    [moduleName.mdJetty]: roleGroup.R,
    [moduleName.kontrakPemberiKerjaPangsu]: roleGroup.ARE,
    [moduleName.kontrakMitraPangsu]: roleGroup.ARE,
    [moduleName.jadwalShipmentPangsu]: roleGroup.ARE,
    [moduleName.operasiShipmentPangsu]: roleGroup.ARE,
    [moduleName.operasiPemeliharaanPangsu]: roleGroup.ARE,
    [moduleName.penagihanPangsu]: roleGroup.R,
    [moduleName.pembayaranPangsu]: roleGroup.R,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  [usersRole.pangsu_staff_keuangan]: {
    [moduleName.mdJetty]: roleGroup.R,
    [moduleName.mdMitraKso]: roleGroup.R,
    [moduleName.kontrakPemberiKerjaPangsu]: roleGroup.R,
    [moduleName.kontrakMitraPangsu]: roleGroup.RU,
    [moduleName.jadwalShipmentPangsu]: roleGroup.R,
    [moduleName.operasiShipmentPangsu]: roleGroup.R,
    [moduleName.operasiPemeliharaanPangsu]: roleGroup.R,
    [moduleName.penagihanPangsu]: roleGroup.CRUD,
    [moduleName.pembayaranPangsu]: roleGroup.ARE,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  [usersRole.pangsu_asmen_keuangan]: {
    [moduleName.mdJetty]: roleGroup.R,
    [moduleName.mdMitraKso]: roleGroup.R,
    [moduleName.kontrakPemberiKerjaPangsu]: roleGroup.R,
    [moduleName.kontrakMitraPangsu]: roleGroup.R,
    [moduleName.jadwalShipmentPangsu]: roleGroup.R,
    [moduleName.operasiShipmentPangsu]: roleGroup.R,
    [moduleName.operasiPemeliharaanPangsu]: roleGroup.R,
    [moduleName.penagihanPangsu]: roleGroup.ARE,
    [moduleName.pembayaranPangsu]: roleGroup.ARE,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  [usersRole.pangsu_manajer_cabang]: {
    [moduleName.mdKapal]: roleGroup.R,
    [moduleName.mdMitraKso]: roleGroup.R,
    [moduleName.mdPemberiKerja]: roleGroup.R,
    [moduleName.mdJetty]: roleGroup.R,
    [moduleName.kontrakPemberiKerjaPangsu]: roleGroup.ARE,
    [moduleName.kontrakMitraPangsu]: roleGroup.ARE,
    [moduleName.jadwalShipmentPangsu]: roleGroup.ARE,
    [moduleName.operasiShipmentPangsu]: roleGroup.ARE,
    [moduleName.operasiPemeliharaanPangsu]: roleGroup.ARE,
    [moduleName.penagihanPangsu]: roleGroup.ARE,
    [moduleName.pembayaranPangsu]: roleGroup.ARE,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },

  // tjb
  [usersRole.tjb_staff_operasi]: {
    [moduleName.mdJetty]: roleGroup.R,
    [moduleName.mdJisdor]: roleGroup.R,
    [moduleName.kontrakPemberiKerjaTJB]: roleGroup.R,
    [moduleName.kontrakMitraTJB]: roleGroup.R,
    [moduleName.coordinationMeetingTJB]: roleGroup.R,
    [moduleName.shipmentPlanningTJB]: roleGroup.R,
    [moduleName.shipmentOperationTJB]: roleGroup.R,
    [moduleName.customerInvoiceTJB]: roleGroup.R,
    [moduleName.vendorBillTJB]: roleGroup.R,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  [usersRole.tjb_asmen_operasi]: {
    [moduleName.mdJetty]: roleGroup.R,
    [moduleName.mdJisdor]: roleGroup.R,
    [moduleName.kontrakPemberiKerjaTJB]: roleGroup.R,
    [moduleName.kontrakMitraTJB]: roleGroup.R,
    [moduleName.coordinationMeetingTJB]: roleGroup.R,
    [moduleName.shipmentPlanningTJB]: roleGroup.R,
    [moduleName.shipmentOperationTJB]: roleGroup.R,
    [moduleName.customerInvoiceTJB]: roleGroup.R,
    [moduleName.vendorBillTJB]: roleGroup.R,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  [usersRole.tjb_staff_keuangan]: {
    [moduleName.mdJetty]: roleGroup.R,
    [moduleName.mdJisdor]: roleGroup.R,
    [moduleName.kontrakPemberiKerjaTJB]: roleGroup.R,
    [moduleName.kontrakMitraTJB]: roleGroup.R,
    [moduleName.coordinationMeetingTJB]: roleGroup.R,
    [moduleName.shipmentPlanningTJB]: roleGroup.R,
    [moduleName.shipmentOperationTJB]: roleGroup.R,
    [moduleName.customerInvoiceTJB]: roleGroup.R,
    [moduleName.vendorBillTJB]: roleGroup.R,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  [usersRole.tjb_asmen_keuangan]: {
    [moduleName.mdJetty]: roleGroup.R,
    [moduleName.mdJisdor]: roleGroup.R,
    [moduleName.kontrakPemberiKerjaTJB]: roleGroup.R,
    [moduleName.kontrakMitraTJB]: roleGroup.R,
    [moduleName.coordinationMeetingTJB]: roleGroup.R,
    [moduleName.shipmentPlanningTJB]: roleGroup.R,
    [moduleName.shipmentOperationTJB]: roleGroup.R,
    [moduleName.customerInvoiceTJB]: roleGroup.R,
    [moduleName.vendorBillTJB]: roleGroup.R,
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
  [usersRole.tjb_manajer_cabang]: {
    [moduleName.mdJetty]: roleGroup.R,
    [moduleName.mdJisdor]: roleGroup.R,
    [moduleName.kontrakPemberiKerjaTJB]: roleGroup.R,
    [moduleName.kontrakMitraTJB]: roleGroup.R,
    [moduleName.coordinationMeetingTJB]: roleGroup.R,
    [moduleName.shipmentPlanningTJB]: roleGroup.R,
    [moduleName.shipmentOperationTJB]: roleGroup.R,
    [moduleName.customerInvoiceTJB]: roleGroup.R,
    [moduleName.vendorBillTJB]: roleGroup.R,
  },
}

const wilayah = {
  wilayah1: 'wilayah-1',
  wilayah2: 'wilayah-2',
  wilayah3: 'wilayah-3',
  wilayah4: 'wilayah-4',
}

const jenisKontrak = {
  spal: 'spal',
  kontrakPemberiKerja: 'kontrak_pemberi_kerja',
  kontrakMitraKso: 'kontrak_mitra_kso',
}

const componentConfig = {
  notification: {
    interval: 2500,
    position: NotificationPosition.topRight,
  },
  confirmation: {
    dismiss: {
      enabled: true,
      escapeKey: false,
      outsidePress: false,
      closeButton: false,
    } as ConfirmationDismissType,
    confirm: {
      title: 'Apakah kamu yakin?',
      message: 'Jika melanjutkan maka data yang sudah dimasukan akan hilang!',
    },
    alert: {
      title: 'Berhasil',
      message: 'Konten berhasil dibuat!',
    },
  },
  modal: {
    dismiss: {
      enabled: true,
      escapeKey: false,
      outsidePress: false,
      closeButton: false,
    } as ModalDismissType,
  },
}

const jenisKapal = {
  spb: 'spb',
  vessel: 'vessel',
  tb_bg: 'tb-bg',
}

const jenisKapalDredging = {
  cutterSuctionDredger: 'cutter_suction_dredger',
  trailingSuctionHopperDredger: 'trailing_suction_hopper_dredger',
  clamshellDredger: 'clamshell_dredger',
  backhoeDredger: 'backhoe_dredger',
  bucketLadderDredger: 'bucket_ladder_dredger',
  grabDradger: 'grab_dredger',
  bucketWheelDredger: 'bucket_wheel_dredger',
  waterInjectionDredger: 'water_injection_dredger',
  airliftDredger: 'airlift_dredger',
}

const tipeKapal = {
  spb: 'spb',
  vessel: 'vessel',
  tb: 'tb',
  bg: 'bg',
}

const kepemilikan = {
  sendiri: 'sendiri',
  kontrak: 'kontrak',
}

const schema_bbm = {
  schema_1: 1,
  schema_2: 2,
  schema_3: 3,
  schema_4: 4,
}

const format_durasi = {
  durasi_hari: 'durasi_hari',
  loading_rate: 'loading_rate',
}

const options = {
  jenisBadanUsaha: [
    {
      label: 'PT',
      value: 'PT',
    },
    {
      label: 'CV',
      value: 'CV',
    },
    {
      label: 'LTD',
      value: 'LTD',
    },
  ],
  kepemilikan: [
    {
      label: 'Milik Sendiri',
      value: kepemilikan.sendiri,
    },
    {
      label: 'Mitra',
      value: kepemilikan.kontrak,
    },
  ],
  wilayah: [
    {
      label: 'Wilayah I',
      value: wilayah.wilayah1,
    },
    {
      label: 'Wilayah II',
      value: wilayah.wilayah2,
    },
    {
      label: 'Wilayah III',
      value: wilayah.wilayah3,
    },
    {
      label: 'Wilayah IV',
      value: wilayah.wilayah4,
    },
  ],
  role: [
    {
      label: 'Sys Admin',
      value: usersRole.sysadmin,
    },
    {
      label: 'Staff Asuransi',
      value: usersRole.staffKeuangan,
    },
    {
      label: 'Manager Asuransi',
      value: usersRole.managerKeuangan,
    },
    {
      label: 'Staff Kemitraan',
      value: usersRole.staffKemitraan,
    },
    {
      label: 'Manager Kemitraan',
      value: usersRole.managerKemitraan,
    },
    {
      label: 'Staff PU',
      value: usersRole.staffPu,
    },
    {
      label: 'Manager PU',
      value: usersRole.managerPu,
    },
    {
      label: 'Staff Penjualan',
      value: usersRole.staffPenjualan,
    },
    {
      label: 'Manager Penjualan',
      value: usersRole.managerPenjualan,
    },
    {
      label: 'VP Niaga',
      value: usersRole.vpNiaga,
    },
    {
      label: 'Staff Operasi',
      value: usersRole.staffOperasi,
    },
    {
      label: 'Manager Operasi',
      value: usersRole.managerOperasi,
    },
    {
      label: 'VP Operasi',
      value: usersRole.vpOperasi,
    },
    {
      label: 'Mitra KSO',
      value: usersRole.mitraKso,
    },
    {
      label: 'Agen Asuransi',
      value: usersRole.agenAsuransi,
    },
    {
      label: 'Auditor Internal',
      value: usersRole.internalAuditor,
    },
    {
      label: 'Pangkalan Susu - Staff Operasi',
      value: usersRole.pangsu_staff_operasi,
    },
    {
      label: 'Pangkalan Susu - Asisten Manager Operasi',
      value: usersRole.pangsu_asmen_operasi,
    },
    {
      label: 'Pangkalan Susu - Staff Keuangan',
      value: usersRole.pangsu_staff_keuangan,
    },
    {
      label: 'Pangkalan Susu - Asisten Manager Keuangan',
      value: usersRole.pangsu_asmen_keuangan,
    },
    {
      label: 'Pangkalan Susu - Manager Cabang',
      value: usersRole.pangsu_manajer_cabang,
    },
    {
      label: 'Staff Operasi Tanjung Jati B.',
      value: usersRole.tjb_staff_operasi,
    },
    {
      label: 'Assisten Manager Operasi Tanjung Jati B.',
      value: usersRole.tjb_asmen_operasi,
    },
    {
      label: 'Staff Keuangan Tanjung Jati B.',
      value: usersRole.tjb_staff_keuangan,
    },
    {
      label: 'Assisten Manager Keuangan Tanjung Jati B.',
      value: usersRole.tjb_asmen_keuangan,
    },
    {
      label: 'Manager Cabang Tanjung Jati B.',
      value: usersRole.tjb_manajer_cabang,
    },
  ],
  schema_bbm: [
    {
      label: 'Skema 1 BBM',
      value: schema_bbm.schema_1,
    },
    {
      label: 'Skema 2 BBM',
      value: schema_bbm.schema_2,
    },
    {
      label: 'Skema Paiton',
      value: schema_bbm.schema_3,
    },
    {
      label: 'Skema SLBM',
      value: schema_bbm.schema_4,
    },
  ],
  format_durasi_demurrage: [
    {
      label: 'Durasi Hari',
      value: format_durasi.durasi_hari,
    },
    {
      label: 'Loading Rate',
      value: format_durasi.loading_rate,
    },
  ],
  kuartal: [
    {
      label: 'I',
      value: '1',
    },
    {
      label: 'II',
      value: '2',
    },
    {
      label: 'III',
      value: '3',
    },
    {
      label: 'IV',
      value: '4',
    },
  ],
  month: [
    {
      label: 'Januari',
      value: '1',
    },
    {
      label: 'Februari',
      value: '2',
    },
    {
      label: 'Maret',
      value: '3',
    },
    {
      label: 'April',
      value: '4',
    },
    {
      label: 'Mei',
      value: '5',
    },
    {
      label: 'Juni',
      value: '6',
    },
    {
      label: 'Juli',
      value: '7',
    },
    {
      label: 'Agustus',
      value: '8',
    },
    {
      label: 'September',
      value: '9',
    },
    {
      label: 'Oktober',
      value: '10',
    },
    {
      label: 'November',
      value: '11',
    },
    {
      label: 'Desember',
      value: '12',
    },
  ],
  kontrak_mitra_shiplink: [
    {
      label: 'SPAL',
      value: 'spal',
    },
    {
      label: 'Kontrak Mitra',
      value: 'kontrak',
    },
    {
      label: '-',
      value: 'bag',
    },
  ],
  timeline_shiplink: [
    {
      label: 'Timeline Pengiriman',
      value: 'pengiriman',
    },
    {
      label: 'Timeline Penagihan',
      value: 'penagihan',
    },
    {
      label: 'Timeline Pembayaran',
      value: 'pembayaran',
    },
  ],
  statusShipmentPemberiKerja: [
    {
      label: 'Pendapatan',
      value: 'penagihan',
    },
    {
      label: 'Shipment',
      value: 'shipment',
    },
    {
      label: 'Volume',
      value: 'volume_bl',
    },
  ],
  jenisOperasiDredgingOptions: [
    {
      label: 'Konsorsium',
      value: 'konsorsium',
    },
    {
      label: 'PT BAG',
      value: 'pt_bag',
    },
  ],
}

const maskCurrency = {
  rupiah: {
    mask: Number,
    radix: ',',
    scale: 2,
    signed: true,
    thousandsSeparator: ',',
    padFractionalZeros: false,
    normalizeZeros: true,
  },
}

const monthsLabelsChart = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Agust',
  'Sept',
  'Oct',
  'Nov',
  'Des',
]

const mitraIsBAg = {
  value: '00000000-0000-0000-0000-000000000000',
  label: 'PT PELAYARAN BAHTERA ADHIGUNA',
}

const zeroUUID = '00000000-0000-0000-0000-000000000000'

const DREDGING_STATUS_OPTIONS = [
  {
    value: CommonDredgingApprovalStatus.DRAFT,
    label: 'Draft',
  },
  {
    value: CommonDredgingApprovalStatus.REVISION,
    label: 'Revisi',
  },
  {
    value: CommonDredgingApprovalStatus.REVIEW_MANAGER,
    label: 'Review Manager',
  },
  {
    value: CommonDredgingApprovalStatus.APPROVED,
    label: 'Approved',
  },
]

const CONTRACT_TYPE_OPTIONS = [
  {
    value: EContractType.INDUK,
    label: 'Kontrak Induk / Payung',
  },
  {
    value: EContractType.SPOT,
    label: 'Kontrak Spot',
  },
]

const METHOD_PAYMENT_SPOT_OPTIONS = [
  {
    value: EMethodPaymentSPOT.FREIGHT,
    label: 'Freight',
  },
  {
    value: EMethodPaymentSPOT.LUMPSUM,
    label: 'Lump Sum',
  },
]

const ARMADA_TYPE_OPTIONS = [
  {
    value: EArmadaType.MILIK,
    label: 'Sendiri / Milik',
  },
  {
    value: EArmadaType.KONTRAK,
    label: 'Mitra',
    [moduleName.arsipAsuransi]: roleGroup.R,
  },
]

export {
  Action,
  API_BASE_URL,
  ARMADA_TYPE_OPTIONS,
  CAPTCHA_SITE_KEY,
  componentConfig,
  CONTRACT_TYPE_OPTIONS,
  DateFormat,
  DREDGING_STATUS_OPTIONS,
  ENABLE_KLAIM_ASURANSI_V2,
  format_durasi,
  jenisKapal,
  jenisKapalDredging,
  jenisKontrak,
  kepemilikan,
  maskCurrency,
  METHOD_PAYMENT_SPOT_OPTIONS,
  mitraIsBAg,
  moduleName,
  monthsLabelsChart,
  NEW_DOMAIN,
  OAUTH_AUTH_API,
  OAUTH_ClIENT_ID,
  OAUTH_CLIENT_SECRET,
  OAUTH_POST_LOGOUT_REDIRECT,
  OAUTH_REDIRECT_LOGOUT,
  OAUTH_REDIRECT_URI,
  OAUTH_TOKEN_API,
  options,
  PANGSU_API_BASE_URL,
  publicRoleMatiks,
  roleAction,
  roleMatriks,
  storageKey,
  tipeKapal,
  usersRole,
  wilayah,
  zeroUUID,
}
